import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment';

import lodashOmit from 'lodash/fp/omit';

import { LISTING_STATUS_ACTIVE, LISTING_STATUS_NOT_AVAILABLE } from 'common/listingConstants';
import { PROPERTY_TYPE_OPTIONS_VALUES } from 'common/propertyTypes';
import { SR_API_ACTION_ADMIN_OVERRIDE_LISTING_DATA } from 'api/actionTypes';

import SrAdminEditListingFormField from 'client/components/SrAdminEditListingFormField';

const formItems = [
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    optionItems: [
      {
        label: 'Active',
        value: LISTING_STATUS_ACTIVE,
      },
      {
        label: 'Leased/Not Available',
        value: LISTING_STATUS_NOT_AVAILABLE,
      },
    ],
  },
  {
    name: 'price',
    label: 'Price',
    type: 'number',
  },
  {
    name: 'applicationFee',
    label: 'Application Fee',
    type: 'number',
  },
  {
    name: 'monthlyPetRent',
    label: 'Monthly Pet Rent',
    type: 'number',
  },
  {
    name: 'petDeposit',
    label: 'Pet Deposit',
    type: 'number',
  },
  {
    name: 'securityDeposit',
    label: 'Security Deposit',
    type: 'number',
  },
  {
    name: 'apartmentUnits',
    label: 'Apartment Units',
    type: 'array',
  },
  {
    name: 'petFee',
    label: 'Pet Fee',
    type: 'number',
  },
  {
    name: 'petsAllowed',
    label: 'Pets Allowed',
    type: 'select',
    optionItems: [
      {
        label: 'True',
        value: true,
      },
      {
        label: 'False',
        value: false,
      },
    ],
  },
  {
    name: 'administrationFee',
    label: 'Administration Fee',
    type: 'number',
  },
  {
    name: 'addressStreetDisplay',
    label: 'Address Street Display',
    type: 'text',
  },
  {
    name: 'addressStreetName',
    label: 'Address Street Name',
    type: 'text',
  },
  {
    name: 'addressStreetNumber',
    label: 'Address Street Number',
    type: 'text',
  },
  {
    name: 'addressZip',
    label: 'Address Zip',
    type: 'text',
  },
  {
    name: 'addressCity',
    label: 'Address City',
    type: 'text',
  },
  {
    name: 'propertyType',
    label: 'Property Type',
    type: 'select',
    optionItems: PROPERTY_TYPE_OPTIONS_VALUES.map((propertyType) => ({
      label: propertyType,
      value: propertyType,
    })),
  },
  {
    name: 'bathrooms',
    label: 'Bathrooms',
    type: 'number',
  },
  {
    name: 'bedrooms',
    label: 'Bedrooms',
    type: 'number',
  },
  {
    name: 'maximumPetsAllowed',
    label: 'Maximum Pets Allowed',
    type: 'number',
  },
  {
    name: 'apartmentOfficeHours',
    label: 'Apartment Office Hours',
    type: 'json',
  },
  {
    name: 'apartmentEmail',
    label: 'Apartment Email',
    type: 'string',
  },
  {
    name: 'availableDate',
    label: 'Available Date',
    type: 'date',
  },
  {
    name: 'listingManagementCompany',
    label: 'Management Company',
    type: 'text',
  },
  {
    name: 'isSyndicated',
    label: 'Is Syndicated',
    type: 'select',
    optionItems: [
      {
        label: 'False',
        value: false,
      },
      {
        label: 'True',
        value: true,
      },
    ],
  },
  {
    name: 'lockboxCode',
    label: 'Lockbox Code',
    type: 'text',
  },
  {
    name: 'notesForShowing',
    label: 'Notes For Showing',
    type: 'long_text',
  },
];

class SrAdminEditListingForm extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
  };

  _onUpdateFormField = (name, value) => {
    const { listing, dispatch } = this.props;

    dispatch(({ state, apiClient }) =>
      apiClient.postWithState({
        action: SR_API_ACTION_ADMIN_OVERRIDE_LISTING_DATA,
        queryId: `update-${name}`,
        state: state,
        payload: {
          listingId: listing.id,
          overrides: {
            ...listing.overrides,
            [name]: {
              value: value,
              createdDate: moment().toISOString(),
            },
          },
        },
        onSuccess: (listingUpdated) => {
          state.set(['listing'], listingUpdated);
        },
      }),
    );
  };

  _onRemoveOverride = (name) => {
    const { listing, dispatch } = this.props;
    const overrides = lodashOmit([name], listing.overrides);

    dispatch(({ state, apiClient }) =>
      apiClient.postWithState({
        action: SR_API_ACTION_ADMIN_OVERRIDE_LISTING_DATA,
        queryId: `remove-${name}`,
        state: state,
        payload: {
          listingId: listing.id,
          overrides: overrides,
        },
        onSuccess: (listingUpdated) => {
          state.set(['listing'], listingUpdated);
        },
      }),
    );
  };

  render() {
    const { listing } = this.props;

    const getItemValue = ({ name, type }) => {
      switch (type) {
        case 'array':
          return JSON.stringify(listing[name] || []);
        case 'json':
          return JSON.stringify(listing[name] || {});
        default:
          return listing[name] || '';
      }
    };

    const initialValues = formItems.reduce(
      (memo, { name, type }) => {
        return {
          ...memo,
          [name]: getItemValue({ name, type }),
        };
      },
      { id: listing.id },
    );

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        isInitialValid={({ initialValues }) => {
          return initialValues.id === listing.id;
        }}
        render={({ values, errors }) => (
          <Form>
            {formItems.map((formItem) => (
              <SrAdminEditListingFormField
                key={formItem.name}
                formItem={formItem}
                onUpdate={this._onUpdateFormField}
                onRemoveOverride={this._onRemoveOverride}
                currentValue={getItemValue(formItem)}
                newValue={values[formItem.name]}
                errorMessage={errors[formItem.name]}
                isOverriden={!!listing.overrides[formItem.name]}
              />
            ))}
          </Form>
        )}
      />
    );
  }
}

SrAdminEditListingForm.displayName = 'SrAdminEditListingForm';

export default SrAdminEditListingForm;
